// src/crm/hooks/useDeals.ts
import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import type { Deal, DealStage, CompModel } from '../../lib/types'

/** Payload pour créer un deal — reflète dealCreateSchema (sans briefUrl, hors périmètre Plan 3) */
export type CreateDealInput = {
  creatorId: string
  appId: string
  campaignId?: string | null
  stage: DealStage
  compModel?: CompModel | null
  amount?: number | null
  currency?: string
  promoCode?: string | null
  affiliateRate?: number | null
  dueDate?: string | null
}

/** Payload partiel pour mettre à jour un deal — reflète dealUpdateSchema */
export type UpdateDealInput = Partial<Omit<CreateDealInput, 'creatorId' | 'appId'>>

export function useDeals(appId: string | null) {
  return useQuery<Deal[]>({
    queryKey: ['deals', appId],
    queryFn: () =>
      api
        .get<{ deals: Deal[] }>(`/api/deals?appId=${appId}`)
        .then((r) => r.deals),
    enabled: appId !== null,
  })
}

export function useDeal(id: string | null) {
  return useQuery<Deal>({
    queryKey: ['deal', id],
    queryFn: () =>
      api.get<{ deal: Deal }>(`/api/deals/${id}`).then((r) => r.deal),
    enabled: id !== null,
  })
}

export function useCreateDeal(queryClient: QueryClient) {
  return useMutation({
    mutationFn: (data: CreateDealInput) =>
      api.post<{ deal: Deal }>('/api/deals', data).then((r) => r.deal),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['deals', variables.appId] })
      void queryClient.invalidateQueries({ queryKey: ['creator'] })
    },
  })
}

export function useUpdateDeal(queryClient: QueryClient) {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDealInput }) =>
      api.put<{ deal: Deal }>(`/api/deals/${id}`, data).then((r) => r.deal),
    onSuccess: (deal) => {
      void queryClient.invalidateQueries({ queryKey: ['deals', deal.appId] })
      void queryClient.invalidateQueries({ queryKey: ['deal', deal.id] })
    },
  })
}

export function useDeleteDeal(queryClient: QueryClient) {
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: boolean }>(`/api/deals/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['deals'] })
    },
  })
}

export type CreateDealsBatchInput = {
  appId: string
  campaignId: string
  deals: { creatorId: string; amount: number | null }[]
}

/** Création en batch depuis l'estimateur UGC (POST /api/deals/batch, atomique). */
export function useCreateDealsBatch() {
  const qc = useQueryClient()
  return useMutation<{ created: number }, Error, CreateDealsBatchInput>({
    mutationFn: (body) => api.post<{ created: number }>('/api/deals/batch', body),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ['deals', variables.appId] })
    },
  })
}

export function usePatchDealStage(appId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, stage }: { id: string; stage: DealStage }) =>
      api.patch<{ deal: Deal }>(`/api/deals/${id}`, { stage }).then((r) => r.deal),
    onMutate: async ({ id, stage }) => {
      await qc.cancelQueries({ queryKey: ['deals', appId] })
      const previous = qc.getQueryData<Deal[]>(['deals', appId])
      qc.setQueryData<Deal[]>(['deals', appId], (old) =>
        old?.map((d) => (d.id === id ? { ...d, stage } : d)) ?? [],
      )
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous !== undefined) {
        qc.setQueryData(['deals', appId], ctx.previous)
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['deals', appId] })
    },
  })
}
